import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchContactDetails,
  updateContact,
  toggleContactEnabled,
  deleteContact,
  type UpdateContactRequest,
} from '../api/contacts';
import { employeesKeys } from './useEmployees';

export const contactKeys = {
  all: ['contacts'] as const,
  detail: (companyId: string, contactId: string) =>
    [...contactKeys.all, 'detail', companyId, contactId] as const,
};

export function useContactDetails(companyId: string, contactId: string) {
  return useQuery({
    queryKey: contactKeys.detail(companyId, contactId),
    queryFn: () => fetchContactDetails(companyId, contactId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!companyId && !!contactId,
  });
}

export function useUpdateContact(companyId: string, contactId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateContactRequest) => updateContact(companyId, contactId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.detail(companyId, contactId) });
      queryClient.invalidateQueries({ queryKey: employeesKeys.all });
    },
  });
}

export function useToggleContactEnabled(companyId: string, contactId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (isEnabled: boolean) => toggleContactEnabled(companyId, contactId, isEnabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.detail(companyId, contactId) });
      queryClient.invalidateQueries({ queryKey: employeesKeys.all });
    },
  });
}

export function useDeleteContact(companyId: string, contactId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteContact(companyId, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
      queryClient.invalidateQueries({ queryKey: employeesKeys.all });
    },
  });
}
